<script setup>
import {computed, onMounted, ref, watch} from "vue";
import GraphExplorerConfig from "@/views/GraphExplorer/GraphExplorerConfig";
import GraphExplorerDrawer from "@/views/GraphExplorer/GraphExplorerDrawer.vue";
import store from "@/store";
import UseGEStore from "@/views/GraphExplorer/Composables/UseGEStore";
import {GERequestAction} from "@/store/modules/GraphExplorer"
import UseGELayout from "@/views/GraphExplorer/Composables/UseGELayout";
import UseGEGxtMenu from "@/views/GraphExplorer/Composables/UseGEGxtMenu";
import UseGEStyles from "@/views/GraphExplorer/Composables/UseGEStyles";
import UseGEGraph from "@/views/GraphExplorer/Composables/UseGEGraph";
import UseGELogic from "@/views/GraphExplorer/Composables/UseGELogic";
import GraphExplorerNotification from "@/views/GraphExplorer/Components/GraphExplorerNotification.vue";
import UseGELayers from "@/views/GraphExplorer/Composables/UseGELayers";
import UseGEExport from "@/views/GraphExplorer/Composables/UseGEExport";
import GraphExplorerLabelEditor from "@/views/GraphExplorer/Components/GraphExplorerLabelEditor.vue";
import {useRoute, useRouter} from "vue-router/composables";
import {GEEleType} from "@/views/GraphExplorer/Components/Drawer/GEEleType";
import {getCyCenter} from "@/views/GraphExplorer/GEUtils";
import BaseDataModifyButton from "@/components/common/Buttons/BaseDataModifyButton.vue";
import LayoutMenu from "@/views/GraphExplorer/Components/LayoutMenu.vue";

const cytoscape = require('cytoscape');

let cy
let menu

const graphSettings = ref({
    showTxTimeStamp: false
})

const {styleOptions} = UseGEStyles()
const {GESelectedElement, GETransactions} = UseGEStore()
const route = useRoute()
const router = useRouter()

const {
    Activate: layoutActivate,
    layoutOptions,
    reLayoutSelectedAsync,
    reLayoutNodesAsync,
    reLayoutGraph
} = UseGELayout(cytoscape)
const {Activate: gxtActivate} = UseGEGxtMenu(cytoscape)
const {Activate: graphActivate, GraphEdge, GraphTx, GraphNode} = UseGEGraph(cytoscape, reLayoutNodesAsync)
const {Activate: logicActivate, StopPeelChain, FollowOutputAsync, FollowOutputIterativelyAsync} = UseGELogic({GraphTx})
const {Activate: layersActivate, RemoveItemFromCacheById, RefreshNodes} = UseGELayers(cytoscape, graphSettings)
const {ExportPng} = UseGEExport(graphSettings)

// Export the whole graph as a high-resolution PNG. The Graph Explorer renders node
// labels / annotations as HTML overlays which cy.png() can't capture, so ExportPng
// temporarily mirrors them onto the canvas, renders, and reverts (see UseGEExport).
const exportPng = () => ExportPng(cy, graphName.value || "graph")


const loading = ref(false)
let initialGraphName = undefined
const graphName = ref(null)
const graphDesc = ref("Interactive Graph")
const graphId = ref(undefined)
const latestConfigId = ref(undefined)
store.commit('GEReset')
store.commit("SELECT_CURRENCY", {currencyUnit: "BTC"})


onMounted(async () => {
    cy = cytoscape({
        container: document.getElementById('cy'),
        ...GraphExplorerConfig,
        layout: layoutOptions,
        ready: function () {
        },
        style: styleOptions,
    });

    //setup Composables
    layersActivate(cy)
    logicActivate(cy)
    layoutActivate(cy)
    graphActivate(cy)
    gxtActivate(cy)


    if (route.params.id) {
        graphId.value = route.params.id
        loading.value = true
        const graph = await store.dispatch('loadGraphExp', {graphId: graphId.value})
        const latestConfig = graph.configs.find(config => config.description === "Interactive Graph")
        latestConfigId.value = latestConfig.id
        const {
            elements,
            notes,
            cyInfo
        } = latestConfig.json_config.graph

        initialGraphName = graph.name

        cy.add(elements)
        graphName.value = graph.name
        GraphNode.AddOnClick()

        notes?.forEach(x => {
            GraphNode.AddNoteNode(x.text, x.position)
        })

        cy.$(".transaction").forEach(ele => {
            store.commit("GETransactionsAdd", ele.data().typeSpecific)
        })

        cy.pan(cyInfo.pan)
        cy.zoom(cyInfo.zoom)

        loading.value = false
    }
})


const addNodeModel = ref(null)
const fetchAndNode = async () => {
    const clear = () => {
        addNodeModel.value = null
        loading.value = false
    }

    loading.value = true
    const nodeToAdd = addNodeModel.value

    const addressInfoPromise = store.dispatch('getAddressInfo', {params: {currencyUnit: "BTC", address: nodeToAdd}})
    const txInfoPromise = store.dispatch('getTransaction', {params: {currencyUnit: "BTC", txId: nodeToAdd}})

    const results = await Promise.all([addressInfoPromise, txInfoPromise])

    if (results[0]) {
        const addressInfo = await addressInfoPromise
        GraphNode.AddAddressNode(addressInfo.data.address, {
            label: addressInfo.data.label,
            position: getCyCenter(cy),
            addressData: addressInfo.data
        })
        clear()
    } else if (results[1]) {
        const txData = await txInfoPromise
        await GraphTx.AddTxAsync(txData, {
            position: getCyCenter(cy),
            undefined,
            flags: {
                allInputs: false,
                allOutputs: false
            }
        }, false)
        clear()
    } else {
        await store.dispatch("error", "Not found")
        loading.value = false
    }
}

const actionName = ref()
const cancelAction = async () => {
    if (currentAction.value === GERequestAction.OutputFollowIteratively
        || currentAction.value === GERequestAction.OutputFollow) {
        actionName.value = undefined
        StopPeelChain()
    }
}

watch([GraphNode.RefreshedNodes, GraphNode.TotalNodes], () => {
    if (currentAction.value === GERequestAction.NodesRefreshMetadata) {
        actionName.value = `Metadata refresh (${GraphNode.RefreshedNodes.value}/${GraphNode.TotalNodes.value})`
    }
})


const currentAction = ref(undefined)
const actionHandler = async (action, payload, invoker) => {
    currentAction.value = action
    switch (action) {
        case GERequestAction.TxAddition:

            const {x = 0, y = 0} = invoker.position
            const invokerAddressId = invoker.data.type === GEEleType.NodeAddress ? invoker.data.id : undefined
            await GraphTx.AddTxAsync(payload.tx, {position: {x, y}, invokerAddressId, flags: {allOutputs: true}})
            break

        case GERequestAction.TxRemoval:
            GraphTx.RemoveTx(payload.txId)
            break

        case GERequestAction.OutputFollowIteratively:
            actionName.value = "Iterative transaction follow"
            await FollowOutputAsync(payload.output, true)
            actionName.value = undefined
            break

        case GERequestAction.OutputFollow:
            actionName.value = "Transaction follow"
            await FollowOutputAsync(payload.output, false)
            actionName.value = undefined
            break

        case GERequestAction.NodeRerender:
            RemoveItemFromCacheById(payload)
            break

        case GERequestAction.TxExpandInputs:
            await GraphTx.AddTxAsync(payload, {flags: {allInputs: true}})
            break

        case GERequestAction.TxExpandOutputs:
            await GraphTx.AddTxAsync(payload, {flags: {allOutputs: true}})
            break

        case GERequestAction.NodesMerge:
            GraphNode.AddMergeNode(payload)
            break

        case GERequestAction.NodesUnmerge:
            GraphNode.RemoveMergeNode(payload)
            cy.$(":selected").unselect()
            break

        case GERequestAction.NodeChangeLabel:
            await editNodeLabel(payload.id, payload.text)
            RemoveItemFromCacheById(payload.id)
            break

        case GERequestAction.NoteAddition:
            await addNoteNode(payload)
            break

        case GERequestAction.NoteRemoval:
            GraphNode.RemoveNoteNode(payload)
            break

        case GERequestAction.NoteUpdate:
            const newContent = await labelEditorRef.value.Open(payload.text, true)
            if (newContent) {
                GraphNode.EditNoteNode(payload.id, newContent)
            }
            break

        case GERequestAction.AddressRemoval:
            //TODO connected transactions do not update their labels
            GraphNode.RemoveNode(payload)
            break

        case GERequestAction.NodesRefreshMetadata:
            actionName.value = "Metadata refresh"
            await GraphNode.RefreshNodesMetadataAsync();
            RefreshNodes()
            actionName.value = undefined
            break;

        case GERequestAction.NodeRefreshMetadata:
            actionName.value = "Metadata refresh 0/1"
            await GraphNode.RefreshNodeByIdAsync(payload.id);
            RemoveItemFromCacheById(payload.id)
            actionName.value = undefined
            break;

        case GERequestAction.SelectedRemoval:
            GraphNode.RemoveNodes(payload)
            break

        case GERequestAction.NodeAnnotate:
            const newAnnotation = await labelEditorRef.value.Open(payload.annotation, true)
            if (newAnnotation !== false) {
                GraphNode.EditNodeAnnotation(payload.id, newAnnotation)
                RemoveItemFromCacheById(payload.id)
            }
            break;
    }
    currentAction.value = undefined
}

store.commit('GERegisterActorCb', actionHandler)

const labelEditorRef = ref(null)
const editNodeLabel = async (id, text) => {
    const newContent = await labelEditorRef.value.Open(text)
    if (newContent) {
        cy.$id(id).data('customLabel', newContent)
    }
}

const addNoteNode = async (position) => {
    const newContent = await labelEditorRef.value.Open("", true)
    if (newContent) {
        GraphNode.AddNoteNode(newContent, position)
    }
}

const onRefreshMetadataClick = async () => {
    await store.dispatch('GERequestAction', {
        actionType: GERequestAction.NodesRefreshMetadata,
        payload: undefined,
        invoker: undefined
    })
}

const OnTimestampsToggle = () => {
    graphSettings.value.showTxTimeStamp = !graphSettings.value.showTxTimeStamp
    RefreshNodes()
}

const createGraphForSaving = () => {
    const elements = cy.elements().not(".note").jsons()
    const notes = []
    for (const noteNode of cy.nodes(".note").jsons()) {
        notes.push({
            position: noteNode.position,
            text: noteNode.data.text
        })
    }
    const cyInfo = {
        pan: {
            x: cy.pan().x,
            y: cy.pan().y,
        },
        zoom: cy.zoom()
    }
    return {
        elements: elements,
        notes: notes,
        cyInfo: cyInfo,
    }
}

const saveGraph = async () => {
    loading.value = true

    const graphToSave = createGraphForSaving()

    if (!graphName.value) {
        graphName.value = await labelEditorRef.value.Open("new graph", false, "Graph name")
    }

    if (!graphName.value) {
        loading.value = false
        return
    }

    if (initialGraphName !== undefined && initialGraphName !== graphName.value && graphId.value) {
        await store.dispatch("editGraph", {graph: {id: graphId.value,name: graphName.value, description: "Interactive Graph", is_hidden: true}})
    }

    const response = await store.dispatch('saveGraph', {
        name: graphName.value,
        initialName: initialGraphName,
        description: graphDesc.value,
        graphId: graphId.value,
        graph: graphToSave
    })

    if (!response)
        return

    graphId.value = response.graphId
    latestConfigId.value = response.configId

    if (!route.params.id) {
        await router.push({params: {id: graphId.value}})
    }
    loading.value = false
}

const graphExportType = ref("save")

const AddressExportUrl = computed(() => `/jwt/v1/export/BTC/graphs/${graphId.value}/config/${latestConfigId.value}/${graphExportType.value}-addresses-csv`)
const TransactionsExportUrl = computed(() => `/jwt/v1/export/BTC/graphs/${graphId.value}/config/${latestConfigId.value}/${graphExportType.value}-transactions-csv`)

const onTxsExport = async () => {
    await saveGraph()
    window.open(TransactionsExportUrl.value, '_blank');
}

const onAddrsExport = async () => {
    await saveGraph()
    window.open(AddressExportUrl.value, '_blank');
}

const onLayoutClick = ({layering, placement, hierarchy, inLayerSpacing}) => {
    reLayoutGraph({
        nodeLayering: layering,
        nodePlacement:placement, hierarchy,
        layoutHierarchy: hierarchy,
        inLayerSpacing: inLayerSpacing,
    })
}

</script>

<template>
    <div style="position: relative; height: 100%; overflow: hidden">
        <GraphExplorerDrawer/>
        <v-text-field
            v-model="graphName"
            class="fontMonospace elevation-0"
            dense
            outlined
            placeholder="New graph name"
            prepend-inner-icon="edit"
            solo
            style="width: fit-content;font-size: 14px;margin-top: 4px;margin-bottom: -10px;margin-left: 13px; z-index: 1"
        >
        </v-text-field>
        <v-text-field
            v-model="addNodeModel"
            :loading="loading"
            class="position-absolute fontMonospace"
            clear-icon="mdi-clear-circle"
            clearable
            color="info"
            dense
            hide-details
            label="Search"
            outlined
            placeholder="Search Tx/Address"
            style="min-width: 500px; max-width: 500px; top: 12px; left: 12px; background-color: #FFFFFF; z-index: 1"
            @keydown.enter="fetchAndNode"
        >
            <template #progress>
                <v-progress-linear :active="loading" absolute color="info" height="6px" indeterminate/>
            </template>
        </v-text-field>
        <div class="d-flex flex-column" style="width: fit-content; position: relative; z-index: 1; top: 30px">
            <div
                class="d-flex flex-column justify-center align-center rounded-lg rounded-l-0 pt-1 pb-2 elevation-2 border"
                style="width: 66px; background-color: #fefefe; gap: 14px;">
                <div class="d-flex align-center justify-center flex-column">
                    <BaseDataModifyButton color="info" icon large @click="saveGraph">
                        <v-icon style="font-size: 24px">
                            save
                        </v-icon>
                    </BaseDataModifyButton>
                    <div class="caption mt-n2" style="font-size: 0.70rem !important">
                        SAVE
                    </div>
                </div>
                <v-divider class="mt-n1 mb-n2 width100"/>
                <div class="text-center">
                    <v-menu
                        :close-on-content-click="false"
                        offset-x
                    >
                        <template v-slot:activator="{ on, attrs }">
                            <div class="d-flex align-center justify-center flex-column">
                                <v-btn color="info" icon large v-bind="attrs" v-on="on">
                                    <v-icon style="font-size: 24px">
                                        mdi-export
                                    </v-icon>
                                </v-btn>
                                <div class="caption mt-n2 text-center" style="font-size: 0.70rem !important; line-height: 0.9rem;">
                                    SAVE&nbsp;& EXPORT
                                </div>
                            </div>
                        </template>
                        <v-list>
                            <v-subheader class="ml-1" style="height: 24px">Export options</v-subheader>
                            <div class="width100 d-flex align-center justify-center mb-1">
                                <v-btn-toggle color="info" v-model="graphExportType" dense>
                                    <v-btn value="show" x-small>
                                        Show
                                    </v-btn>
                                    <v-btn value="save" x-small>
                                        Save
                                    </v-btn>
                                </v-btn-toggle>
                            </div>
                            <v-divider class="width100"/>
                            <v-list-item @click="onAddrsExport">
                                Addresses
                            </v-list-item>
                            <v-list-item @click="onTxsExport">
                                Transactions
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
                <v-divider class="my-n1 width100"/>
                <LayoutMenu @on-layout-event="onLayoutClick"/>
                <v-divider class="my-n1 width100"/>
                <div class="d-flex align-center justify-center flex-column">
                    <v-btn class="" color="info" icon large @click="onRefreshMetadataClick">
                        <v-icon style="font-size: 24px">
                            mdi-database-refresh-outline
                        </v-icon>
                    </v-btn>
                    <div class="caption mt-n2" style="font-size: 0.70rem !important">
                        REFRESH
                    </div>
                </div>
                <v-divider class="my-n1 width100"/>
                <div class="d-flex align-center justify-center flex-column">
                    <v-btn color="info" icon large @click="OnTimestampsToggle">
                        <v-icon v-if="!graphSettings.showTxTimeStamp" style="font-size: 24px">
                            mdi-timer-outline
                        </v-icon>
                        <v-icon v-if="graphSettings.showTxTimeStamp" style="font-size: 24px">
                            mdi-timer-off-outline
                        </v-icon>
                    </v-btn>
                    <div class="caption mt-n2" style="font-size: 0.70rem !important">
                        TX TIMES
                    </div>
                </div>
                <v-divider class="my-n1 width100"/>
                <div class="d-flex align-center justify-center flex-column">
                    <v-btn color="info" icon large @click="exportPng">
                        <v-icon style="font-size: 24px">
                            save_alt
                        </v-icon>
                    </v-btn>
                    <div class="caption mt-n2 text-center" style="font-size: 0.70rem !important; line-height: 0.9rem;">
                        EXPORT PNG
                    </div>
                </div>
            </div>
        </div>
        <div v-once id="cy" ref="cyRef" class="cyContainer">
        </div>
        <GraphExplorerLabelEditor ref="labelEditorRef"/>
        <GraphExplorerNotification
            :hideCancel="currentAction === GERequestAction.NodesRefreshMetadata || currentAction === GERequestAction.NodeRefreshMetadata"
            :text="actionName"
            @on-cancel-event="cancelAction"/>
    </div>
</template>

<style>


.category-container {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 28px;
    width: 28px;
    background-color: #f0f0f0;
    border-radius: 50%;
    //border: solid 1px grey;
}

.cyContainer {
    width: 100%;
    height: 100%;
    display: block;
}

.noteContent {
    -moz-border-radius: 10px;
    -webkit-border-radius: 10px;
    -webkit-box-shadow: 0px 0 3px rgba(0, 0, 0, 0.25);
    -moz-box-shadow: 0px 0 3px rgba(0, 0, 0, 0.25);
    box-shadow: 0px 0 3px rgba(0, 0, 0, 0.25);
    background-color: #eee;
    padding: 8px;
    border: 1px solid gray;
    white-space: pre;
}

#cy {
    width: 100%;
    height: 100%;
    position: absolute;
    left: 0;
    top: 0;
}

.cxtmenu-content {
    //background-color: #1f9d55;
}

/* you can set the disabled style how you like on the text/icon */
.cxtmenu-disabled {
    opacity: 0.333;
}


</style>